import { AsyncPipe, Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnDestroy,
  OnInit,
  inject,
  viewChild,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { initSentry } from '@app/providers/sentry.providers';
import { BsModalService } from 'ngx-bootstrap/modal';
import { fromEvent } from 'rxjs';
import { debounceTime, filter, pairwise } from 'rxjs/operators';

import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ErrorHandlerService } from '@app/_services/errorHandler.service';
import { AngularReactService } from '@app/react/bridge';
import { getGlobalProviders } from '@app/react/GlobalProviders';
import { setAngularInjector } from '@app/react/utils/angularInjector';
import { TranslateService } from '@ngx-translate/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { appConfig } from 'config/appConfig';
import posthog from 'posthog-js';
import { loadTranslations } from '../i18n/translations';
import { NgAceSidebar } from './_ace/directives/ace-sidebar.directive';
import { NavbarComponent } from './_controls/navbar/navbar.component';
import { SidebarComponent } from './_controls/sidebar/sidebar.component';
import { AccountService } from './_services/account.service';
import { ApplicationService } from './_services/application.service';
import { RefreshableView } from './_views/refreshable-view';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NavbarComponent, SidebarComponent, NgAceSidebar, RouterOutlet, AsyncPipe],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly applicationService = inject(ApplicationService);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly modalService = inject(BsModalService);
  private readonly titleService = inject(Title);
  private readonly oauthService = inject(OAuthService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly destroyRef = inject(DestroyRef);

  readonly sidebar = viewChild.required<NgAceSidebar>('sidebar');
  protected readonly accountService = inject(AccountService);

  hideNavbar = false;
  hideSidebar = false;

  private currentComponent?: Component;
  private previousUrl?: string;

  footerImage = appConfig.footerImageUrl;

  constructor() {
    const angularReact = inject(AngularReactService);
    const injector = inject(Injector);

    setAngularInjector(injector);
    loadTranslations(this.translate);

    let userLanguage = localStorage.getItem('userLanguage');

    if (!userLanguage) {
      userLanguage = this.getSupportedBrowserLanguage();
    }

    this.accountService.setUserLanguage(userLanguage);

    angularReact.wrappers.push(getGlobalProviders(appConfig.theme ?? ''));
  }

  getSupportedBrowserLanguage(): string {
    const browserCultureLang = this.translate.getBrowserCultureLang();
    const supportedLanguages = this.translate.getLangs();

    if (browserCultureLang && supportedLanguages.includes(browserCultureLang)) {
      return browserCultureLang;
    }

    const browserLang = this.translate.getBrowserLang();
    if (browserLang) {
      const matchingLang = supportedLanguages.find((lang) => lang.startsWith(browserLang));
      if (matchingLang) {
        return matchingLang;
      }
    }

    // Fallback to default
    return this.translate.getFallbackLang() ?? '';
  }

  ngOnInit() {
    this.setCurrentUser();

    this.applicationService
      .getApplicationConfig()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        initSentry(result);

        if (result.posthogApiKey) {
          posthog.init(result.posthogApiKey, {
            api_host: 'https://eu.i.posthog.com',
            defaults: '2025-05-24',
            person_profiles: 'identified_only',
          });
          this.accountService.currentJwt$
            .pipe(pairwise(), takeUntilDestroyed(this.destroyRef))
            .subscribe(([prevToken, token]) => {
              if (token) {
                posthog.identify(token.sub);
              } else if (prevToken) {
                posthog.reset();
              }
            });
        }

        // initialize oAuth service
        this.oauthService.configure({
          clientId: 'core-' + result.flavor,
          redirectUri: globalThis.location.origin,
          issuer: result.oauthIssuerUrl,
          responseType: 'code',
          scope: 'openid profile email',
          tokenEndpoint: result.oauthIssuerUrl + '/protocol/openid-connect/token',
        });

        this.oauthService.setupAutomaticSilentRefresh();
        this.oauthService
          .loadDiscoveryDocumentAndTryLogin()
          .then(() => {
            this.accountService.markAuthInitialized();
            this.accountService.navigateToReturnUrlAfterLogin();
          })
          .catch((error: unknown) => {
            this.accountService.markAuthInitialized();

            if (error instanceof HttpErrorResponse) {
              this.errorHandler.displayErrorDialog(error);
              return;
            }

            // angular-oauth2-oidc rejects with { type: 'code_error', reason, params }
            // when Keycloak returns ?error=… in the callback (e.g. auth session expired
            // because the user sat on the login page too long).
            if (
              typeof error === 'object' &&
              error !== null &&
              'type' in error &&
              (error as { type: unknown }).type === 'code_error'
            ) {
              // clear the error query params so a refresh doesn't re-trigger anything
              this.router.navigate(['/account/login'], { replaceUrl: true });
            }
          });

        // check if user logged out on other tab — debounced so a brief token
        // absence during a cross-tab silent refresh doesn't trigger a false logout
        fromEvent<StorageEvent>(globalThis, 'storage')
          .pipe(
            filter((event) => event.storageArea === localStorage),
            debounceTime(2000),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe(() => {
            if (!this.oauthService.hasValidAccessToken()) {
              this.accountService.logout();
            }
          });
      });

    // Subscribe to application status to lockout users if applicaiton is locked.
    this.applicationService.subscribeApplicationStatus();

    // Subscribe to router events so we can reload components when the same routerlink is clicked again.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((navigationEnd: NavigationEnd) => {
        if (this.instanceOfRefreshableView(this.currentComponent) && this.previousUrl == navigationEnd.url) {
          this.currentComponent.refreshView();
        }

        this.previousUrl = navigationEnd.url;
      });

    this.translate
      .get('Nav.Title')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((title) => {
        this.titleService.setTitle(title);
      });
  }

  instanceOfRefreshableView(view: unknown): view is RefreshableView {
    if (view && typeof view === 'object') {
      return 'refreshView' in view;
    }

    return false;
  }

  ngAfterViewInit() {
    // The is-document-loaded class is needed for Ace to enable transitions.
    document.body.classList.add('is-document-loaded');
  }

  ngOnDestroy() {
    this.applicationService.unsubscribeApplicationStatus();
  }

  setCurrentComponent(componentRef: Component): void {
    this.currentComponent = componentRef;
  }

  setCurrentUser() {
    // Save the current url location so we can navigate back in case we can get a new access token.
    const currentUrl = this.location.path();
    if (this.oauthService.hasValidAccessToken()) {
      this.accountService.setUsernameFromToken();
      this.accountService.updateUserRoles();
      this.accountService.loadUserLanguage();
      this.redirectFromLogin(currentUrl, true);
    }
  }

  /**
   * This function decides if the user should be redirected to the dashboard based on the current url.
   * @param url Current url
   * @param navigateToUrl If set to true and the user is not redirected to the dashboard, this will redirect to the url from the url parameter.
   */
  redirectFromLogin(url: string, navigateToUrl: boolean) {
    if (url == '' || url.startsWith('/account/login')) {
      this.router.navigate(['/home/dashboard']);
    } else if (navigateToUrl) {
      this.router.navigateByUrl(url);
    }
  }
}
